/**
 * What: this is a component that tracks the content view
 * Why: easier to implement as a component and provides greater control over how we track content views
 * How: in the hook we get the current page context, tracking client, pass down the params to the handler -> done
 */
import {
  TrackingInitialPageContext,
  TrackingPageContext,
  trackContentView,
  trackRouteChange,
} from '@aboutyou/legacy-tracking-sdk';

import React, {FC, useEffect} from 'react';
import {useTrackingPageContext} from '../context/PageTrackingContext';
import {useTracking} from '../utils/utils';
import {useConvertUserAndSessionToCustomer} from './TrackCustomer/useConvertUserAndSessionToCustomer';

interface Props {
  breadcrumbsPath?: string;
  pageTitle: string;
  overridePageProps?: {type?: string; id?: number};
  isNotFound?: boolean;
}

export const TrackContentPageView: FC<Props> = props =>
  process.browser ? <TrackContentPageViewClient {...props} /> : <></>;

export const TrackContentPageViewClient: FC<Props> = props => {
  useTrackContentView(props);
  return null;
};

function useTrackContentView({
  breadcrumbsPath,
  pageTitle,
  overridePageProps,
  isNotFound,
}: Props) {
  const pageContext = useTrackingPageContext();
  const {TrackingClient} = useTracking();
  const customer = useConvertUserAndSessionToCustomer();

  // eslint-disable-next-line local-rules/too-complex-useeffects -- TODO: Cleanup complex useEffect https://aboutyou.atlassian.net/browse/ASFE-7605
  useEffect(() => {
    const isContentViewEnabled = disableContentViewForSearchParams(
      pageContext?.search,
    );

    if (pageContext !== null && isContentViewEnabled) {
      const {...page} = pageContext;

      if (customer) {
        // content view for basket flyout triggers before add to basket one which messes up values depending on it
        const timer = setTimeout(() => {
          // We might want to merge route change and content view later.
          // Initially we just added different events as the content view needed data that sometimes needed to be fetched from the backend first.
          // With the new url structure this is not required any longer.
          TrackingClient.track(
            trackRouteChange({
              page: {
                ...page,
                ...overridePageProps,
                title: pageTitle,
              } as TrackingInitialPageContext,
            }),
          );

          TrackingClient.track(
            trackContentView({
              page: {
                ...page,
                ...overridePageProps,
                title: pageTitle,
                // remove this after https://aboutyou.atlassian.net/browse/AYFEP-3321 is fixed
                type: isNotFound ? '404' : page.type,
              } as unknown as TrackingPageContext,
              breadcrumbsPath,
              customer,
            }),
          );
        }, 1000);

        return () => {
          clearTimeout(timer);
        };
      }
    }
  }, [
    pageContext.id,
    pageContext.path,
    pageContext.search,
    pageContext.type,
    TrackingClient,
    breadcrumbsPath,
    pageTitle,
    customer?.type,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */
}

// handles cases of addition of search params on which we dont want to track content view
const disableContentViewForSearchParams = (search: string | undefined) => {
  const searchParamType = getSearchParam(search);

  switch (searchParamType) {
    case 'filter':
      return false;

    default:
      return true;
  }
};

// check if filter flyout is open
const getSearchParam = (search: string | undefined) => {
  if (search) {
    if (search.indexOf('filter=1') > -1) {
      return 'filter';
    }
  } else {
    return undefined;
  }
};
